// SUB-OFFICE 7 — entity registry + event bus. No ECS: a flat ordered array of
// typed modules with init/update/dispose, and a ~30-line bus. Update order is
// the array order; dt is clamped upstream to <=33ms; no per-frame allocation.
//
// BUS EVENTS (contract for all slices; gameplay agent extends, never renames):
//   'chyron'        (text: string)            — print a line on the hall chyron
//   'chyron:line'   (text: string)            — a whole line finished (aria mirror)
//   'context'       (text: string | null)     — bottom context line request
//   'toast'         (text: string)            — print-layer toast in voice
//   'stats'         (blob: StatsBlob)         — 10s /api/stats poll result
//   'interact'      ({ id: string })          — player activated an interactable
//   'telemetry'     ({ kind: string, count: number }) — zone crossings etc.
//   'fsm'           ({ from: GameState, to: GameState })
//   'audio:tick' | 'audio:flap' | 'audio:servo' | 'audio:step' | 'audio:thunk'
//   -- stubs for the gameplay slice (documented, unused here): --
//   'summons'       ({ windowIdx: number })   — window lights + buzz
//   'window:print'  ({ windowIdx, text })     — Form 7A dot-matrix print
//   'window:activate' ({ idx: number })       — E at a clerk window
//   'ceremony:adjudication' ({ tier: 'FULL' | 'EXPEDITED' })
//   'ceremony:verdict' | 'ceremony:purge'     — ceremony director cues

export type BusHandler = (payload?: unknown) => void;

export class EventBus {
  private map = new Map<string, BusHandler[]>();

  on(event: string, fn: BusHandler): () => void {
    let arr = this.map.get(event);
    if (!arr) {
      arr = [];
      this.map.set(event, arr);
    }
    arr.push(fn);
    return () => this.off(event, fn);
  }

  off(event: string, fn: BusHandler): void {
    const arr = this.map.get(event);
    if (!arr) return;
    const i = arr.indexOf(fn);
    if (i >= 0) arr.splice(i, 1);
  }

  emit(event: string, payload?: unknown): void {
    const arr = this.map.get(event);
    if (!arr) return;
    for (let i = 0; i < arr.length; i++) arr[i](payload);
  }
}

export interface Entity {
  readonly id: string;
  update(dt: number): void;
  dispose?(): void;
}

export class EntityRegistry {
  private list: Entity[] = [];

  add(e: Entity): void {
    this.list.push(e);
  }

  update(dt: number): void {
    for (let i = 0; i < this.list.length; i++) this.list[i].update(dt);
  }

  dispose(): void {
    for (const e of this.list) if (e.dispose) e.dispose();
    this.list.length = 0;
  }
}
